<template>
  <v-card class="pa-4">
    <v-card-title class="text-h5 mb-4 text-center font-weight-bold">
      {{ title }}
    </v-card-title>

    <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
      <v-text-field
        v-model="pinCode"
        label="Código PIN"
        :rules="pinRules"
        variant="outlined"
        class="text-center"
        style="font-size: 1.5em; font-family: monospace; letter-spacing: 3px"
        :loading="loading"
        autofocus
        counter
        maxlength="12"
        persistent-hint
      />

      <v-alert v-if="error" type="error" class="mb-4" closable @click:close="clearError">
        {{ getErrorMessage(error) }}
      </v-alert>

      <v-alert v-if="accessData" type="success" class="mb-4 mt-2">
        <div class="text-h6 mb-2">¡PIN válido! Acceso concedido</div>
        <v-divider class="my-3" />
        <div>
          <strong>Grupo:</strong> {{ accessData.group_name }}<br />
          <strong>Expira:</strong> {{ formatDate(accessData.expires_at) }}
        </div>
      </v-alert>

      <v-card-actions class="justify-center">
        <v-btn
          type="submit"
          :loading="loading"
          :disabled="!valid || !pinCode"
          size="large"
          class="px-8 validate-pin-button"
        >
          <v-icon left>mdi-key</v-icon>
          Validar PIN
        </v-btn>
      </v-card-actions>

      <v-card-actions v-if="accessData" class="justify-center mt-4">
        <v-btn color="success" @click="proceedToQuiz" size="large" class="px-8">
          <v-icon left>mdi-play</v-icon>
          Comenzar Evaluación
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

const props = defineProps({
  title: {
    type: String,
    default: 'Validar PIN',
  },
})

const emit = defineEmits(['pin-validated', 'access-granted'])

const router = useRouter()
const store = useStore()

const form = ref()
const valid = ref(false)
const pinCode = ref('')

const loading = computed(() => store.getters['pins/isLoading'])
const error = computed(() => store.getters['pins/getError'])
const accessData = computed(() => store.getters['pins/getAccessData'])

// Validation rules
const pinRules = [
  (v) => !!v || 'El código PIN es requerido',
  (v) => (v && v.length >= 4) || 'El PIN debe tener al menos 4 dígitos',
]

const handleSubmit = async () => {
  if (!valid.value || !pinCode.value) return

  const result = await store.dispatch('session/joinWaitingRoom', pinCode.value.trim())

  if (result.success) {
    await store.dispatch('pins/setAccessData', result.data)

    emit('pin-validated', pinCode.value)
    emit('access-granted', result.data)

    if (result.data.session_status === 'waiting') {
      console.log('Joining waiting room with group_id:', result.data.group_id)
      router.push('/waiting-room')
    } else if (result.data.session_status === 'active') {
      console.log(
        'Session already active, going directly to evaluation with group_id:',
        result.data.group_id,
      )
      if (result.data.group_id) {
        router.push({
          name: 'Evaluation',
          params: { groupId: result.data.group_id },
        })
      } else {
        console.error('No group_id received for active session')
      }
    }
  }
}

const proceedToQuiz = () => {
  if (accessData.value) {
    console.log('Navigating to evaluation with group:', accessData.value.group_id)

    router
      .push({
        name: 'Evaluation',
        params: { groupId: accessData.value.group_id },
      })
      .then(() => {
        console.log('Navigation to evaluation successful')
      })
      .catch((error) => {
        console.error('Navigation to evaluation failed:', error)
      })
  } else {
    console.error('No access data available')
  }
}

const clearError = () => {
  store.dispatch('pins/clearError')
}

const getErrorMessage = (error) => {
  const errorMessages = {
    'PIN not found': 'PIN no encontrado',
    'PIN expired': 'PIN expirado',
    'PIN invalid': 'PIN inválido',
    'Access denied': 'Acceso denegado',
    Unauthorized: 'No autorizado',
  }

  return errorMessages[error] || error || 'Error desconocido'
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<style scoped>
.v-text-field :deep(.v-field__input) {
  text-align: center;
  font-size: 1.5em;
  font-family: 'Courier New', monospace;
  letter-spacing: 3px;
  font-weight: bold;
}

.v-text-field :deep(.v-field__input input) {
  text-align: center;
}

.v-alert .text-h6 {
  color: white;
}

.validate-pin-button {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  border-radius: 8px;
}
</style>
